import React, { useMemo } from "react";
import { useTable, useFilters, useSortBy } from "react-table";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";

import FlagIcon from "@mui/icons-material/Flag";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BalanceIcon from "@mui/icons-material/Balance";
import InfoIcon from "@mui/icons-material/Info";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import DiscountIcon from "@mui/icons-material/Discount";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
    />
  );
}

export default function DialogCampanhaResultados({
  PaperComponent,
  open,
  onClose,
  campanhaResultados,
  resultados,
  resultadosExcluidos,
  alertasCampanha,
  filtroResolucaoAlertas,
  setFiltroResolucaoAlertas,
  gravarResolucaoAlerta,
  abrirResultado,
  reintegrarFatura,
  abrirRecusarFatura,
  abrirRecebimentos,
  limparVendedor,
  loadingResultados,
  soComAlertas,
}) {
  const dadosTabela = Array.isArray(resultados) ? resultados : [];

  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "acoes",
        disableFilters: true,
        width: 72,
        Cell: ({ row }) => {
          const r = row.original;

          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 4,
                whiteSpace: "nowrap",
              }}
            >
              <IconButton
                size="small"
                color="info"
                onClick={() => abrirResultado(r)}
                title="Ver detalhe"
              >
                <InfoIcon fontSize="small" />
              </IconButton>

              {r.estado === "Recusado" ? (
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => reintegrarFatura(r)}
                  title="Reintegrar fatura"
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  color="error"
                  disabled={!["Detetado", "Ajustado", "Solicitado"].includes(r.estado)}
                  onClick={() => abrirRecusarFatura(r)}
                  title="Recusar/excluir fatura"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </div>
          );
        },
      },
      {
        Header: "Alertas",
        id: "alertas",
        disableFilters: true,
        Cell: ({ row }) => {
          const r = row.original;

          return (
            <div style={{ textAlign: "center", whiteSpace: "nowrap" }}>
              {Number(r.sem_flag_campanha || 0) === 1 && (
                <FlagIcon
                  fontSize="small"
                  sx={{ color: "#ed6c02", verticalAlign: "middle" }}
                  titleAccess="Fatura sem flag/código da campanha suficiente"
                />
              )}

              {Number(r.tem_descprom_acima_maximo || 0) === 1 && (
                <DiscountIcon
                  fontSize="small"
                  sx={{ color: "#ed6c02", verticalAlign: "middle", ml: 0.5 }}
                  titleAccess={
                    r.detalhe_descprom_acima_maximo ||
                    "Desconto promocional superior ao máximo indicado pelo fabricante"
                  }
                />
              )}

              {Number(r.tem_diferenca_flag_conjuntos || 0) === 1 && (
                <span
                  title={
                    r.detalhe_diferenca_flag ||
                    "Diferença entre quantidade declarada na flag e conjuntos detetados"
                  }
                >
                  <BalanceIcon
                    fontSize="small"
                    sx={{ color: "#ed6c02", verticalAlign: "middle", ml: 0.5 }}
                  />
                </span>
              )}

              {Number(r.cliente_excluido_alerta || 0) === 1 && (
                <span title={r.cliente_excluido_motivo || "Campanha aplicada em cliente excluído"}>
                  <PersonOffIcon
                    fontSize="small"
                    sx={{ color: "#ed6c02", verticalAlign: "middle", ml: 0.5 }}
                  />
                </span>
              )}
            </div>
          );
        },
      },
      { Header: "Fatura origem", accessor: "faturaorigem", Filter: TextFilter },
      {
        Header: "Cliente",
        accessor: "cliente",
        Filter: TextFilter,
        Cell: ({ row }) => row.original.cliente || row.original.refcliente,
      },
      {
        Header: "Vendedor",
        accessor: "vendedor",
        Filter: TextFilter,
        Cell: ({ row }) => limparVendedor(row.original.vendedor) || row.original.refvendedor,
      },
      {
        Header: "Conjuntos",
        accessor: "conjuntosdetetados",
        Filter: TextFilter,
        Cell: ({ value }) => Number(value || 0),
      },
      {
        Header: "Valor a receber",
        accessor: "valor_a_receber",
        Filter: TextFilter,
        Cell: ({ value }) => Number(value || 0),
      },
      {
        Header: "Valor recebido",
        accessor: "valor_total_recebido",
        Filter: TextFilter,
        Cell: ({ value }) => Number(value || 0),
      },
      {
        Header: "Oferta",
        accessor: "materialoferta",
        Filter: TextFilter,
        Cell: ({ row }) => {
          const r = row.original;

          return r.refmaterialoferta
            ? `${r.qtd_oferta_a_receber || ""} ${r.unidadeoferta || ""} ${
                r.materialoferta || r.refmaterialoferta
              }`
            : "";
        },
      },
      { Header: "Estado", accessor: "estado", Filter: TextFilter },
      {
        Header: "Flag promo",
        id: "flagpromo",
        accessor: (r) =>
          `${Number(r.qtd_flag_total || 0)} / ${Number(r.conjuntosdetetados || 0)}${
            r.faturas_flag_regularizacao
              ? ` — Própria: ${Number(r.qtd_flag_propria || 0)} | Reg.: ${
                  r.faturas_flag_regularizacao
                }`
              : ` — Própria: ${Number(r.qtd_flag_propria || 0)}`
          }`,
        Filter: TextFilter,
      },
    ],
    [
      soComAlertas,
      alertasCampanha,
      filtroResolucaoAlertas,
      abrirResultado,
      reintegrarFatura,
      abrirRecusarFatura,
      limparVendedor,
      gravarResolucaoAlerta,
    ]
  );

  const table = useTable(
    {
      columns,
      data: dadosTabela,
      autoResetFilters: false,
      autoResetSortBy: false,
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = table;

  return (
    <Dialog
      PaperComponent={PaperComponent}
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: "calc(100% - 24px)", lg: "1100px" },
          maxWidth: "100%",
          m: { xs: 1.5, md: 3 },
        },
      }}
    >
      <DialogTitle id="draggable-dialog-title" style={{ cursor: "move" }}>
        Resultados detetados {campanhaResultados?.nome ? `— ${campanhaResultados.nome}` : ""}
      </DialogTitle>

      <DialogContent
        sx={{
          px: { xs: 1.5, md: 3 },
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        {loadingResultados ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="220px">
            <CircularProgress />
          </MDBox>
        ) : (
          <MDBox
            sx={{
              maxHeight: { xs: "62vh", md: 420 },
              overflow: "auto",
              mt: 1,
              maxWidth: "100%",
            }}
          >
            {Array.isArray(resultadosExcluidos) && resultadosExcluidos.length > 0 && (
              <MDBox
                mb={2}
                p={{ xs: 1, md: 1.5 }}
                sx={{
                  background: "#fff5f5",
                  borderRadius: 1,
                  overflowX: "auto",
                  maxWidth: "100%",
                }}
              >
                <strong style={{ fontSize: 13 }}>Faturas excluídas</strong>

                <table
                  style={{
                    width: "100%",
                    minWidth: 780,
                    borderCollapse: "collapse",
                    marginTop: 8,
                  }}
                >
                  <thead>
                    <tr>
                      <th style={thCenter}></th>
                      <th style={thLeft}>Fatura origem</th>
                      <th style={thLeft}>Cliente</th>
                      <th style={thLeft}>Vendedor</th>
                      <th style={thRight}>Valor a receber</th>
                      <th style={thLeft}>Motivo</th>
                    </tr>
                  </thead>

                  <tbody>
                    {resultadosExcluidos.map((r) => (
                      <tr key={`ex-${r.id}`}>
                        <td style={tdAcoes}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => reintegrarFatura(r)}
                            title="Reintegrar fatura"
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </td>
                        <td style={tdBase}>{r.faturaorigem}</td>
                        <td style={tdBase}>{r.cliente || r.refcliente}</td>
                        <td style={tdBase}>{limparVendedor(r.vendedor) || r.refvendedor}</td>
                        <td style={tdRight}>{Number(r.valor_a_receber || 0).toFixed(2)}</td>
                        <td style={tdBase}>{r.motivorecusa || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </MDBox>
            )}

            <table
              {...getTableProps()}
              style={{
                width: "100%",
                minWidth: 1120,
                borderCollapse: "collapse",
              }}
            >
              <thead>
                {headerGroups.map((hg) => (
                  <tr {...hg.getHeaderGroupProps()}>
                    {hg.headers.map((col) => (
                      <th
                        {...col.getHeaderProps(col.getSortByToggleProps())}
                        style={{
                          ...thBase,
                          textAlign: [
                            "conjuntosdetetados",
                            "valor_a_receber",
                            "valor_total_recebido",
                          ].includes(col.id)
                            ? "right"
                            : col.id === "acoes" || col.id === "alertas"
                            ? "center"
                            : "left",
                          width: col.id === "acoes" ? 72 : col.id === "alertas" ? 90 : undefined,
                          minWidth:
                            col.id === "cliente"
                              ? 210
                              : col.id === "vendedor"
                              ? 130
                              : col.id === "flagpromo"
                              ? 230
                              : undefined,
                          verticalAlign: "bottom",
                        }}
                      >
                        <div>
                          {col.render("Header")}
                          {col.isSorted ? (col.isSortedDesc ? " ▼" : " ▲") : null}
                        </div>

                        {!col.disableFilters && (
                          <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                            {col.canFilter ? col.render("Filter") : null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={soComAlertas ? 8 : 11}
                      style={{
                        fontSize: 13,
                        padding: 18,
                        textAlign: "center",
                        color: "#7b809a",
                        borderTop: "1px solid #eee",
                      }}
                    >
                      {soComAlertas
                        ? "Sem alertas para o filtro selecionado."
                        : "Sem faturas detetadas nesta campanha."}
                    </td>
                  </tr>
                )}

                {rows.map((row) => {
                  prepareRow(row);

                  const r = row.original;
                  const temAlerta =
                    Number(r.sem_flag_campanha || 0) === 1 ||
                    Number(r.tem_descprom_acima_maximo || 0) === 1 ||
                    Number(r.cliente_excluido_alerta || 0) === 1 ||
                    Number(r.tem_diferenca_flag_conjuntos || 0) === 1;

                  return (
                    <tr
                      {...row.getRowProps()}
                      style={{
                        color: temAlerta ? "#d32f2f" : undefined,
                        backgroundColor: temAlerta ? "#fff5f5" : undefined,
                      }}
                    >
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          style={{
                            ...tdBase,
                            textAlign: [
                              "conjuntosdetetados",
                              "valor_a_receber",
                              "valor_total_recebido",
                            ].includes(cell.column.id)
                              ? "right"
                              : cell.column.id === "acoes" || cell.column.id === "alertas"
                              ? "center"
                              : "left",
                            width:
                              cell.column.id === "acoes"
                                ? 72
                                : cell.column.id === "alertas"
                                ? 90
                                : undefined,
                            whiteSpace: cell.column.id === "flagpromo" ? "nowrap" : undefined,
                            minWidth:
                              cell.column.id === "cliente"
                                ? 210
                                : cell.column.id === "vendedor"
                                ? 130
                                : cell.column.id === "flagpromo"
                                ? 230
                                : undefined,
                          }}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </MDBox>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexWrap: "wrap",
          gap: 1,
          px: { xs: 2, md: 3 },
          pb: { xs: 2, md: 2 },
          "& .MuiButton-root": {
            width: { xs: "100%", sm: "auto" },
          },
        }}
      >
        <MDButton color="success" onClick={() => abrirRecebimentos(campanhaResultados?.id)}>
          Registar recebimentos
        </MDButton>
        <MDButton color="secondary" onClick={onClose}>
          Fechar
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

const thBase = {
  position: "sticky",
  top: 0,
  zIndex: 3,
  background: "#fff",
  boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
  fontSize: 12,
  padding: 6,
};

const thLeft = {
  ...thBase,
  textAlign: "left",
};

const thRight = {
  ...thBase,
  textAlign: "right",
};

const thCenter = {
  ...thBase,
  textAlign: "center",
  width: 78,
};

const tdBase = {
  fontSize: 12,
  padding: 6,
  borderTop: "1px solid #eee",
};

const tdRight = {
  ...tdBase,
  textAlign: "right",
};

const tdAcoes = {
  ...tdBase,
  textAlign: "center",
  width: 40,
};

const tdAlertas = {
  ...tdBase,
  textAlign: "center",
  width: 58,
  whiteSpace: "nowrap",
};
